import { queryOptions, useQuery } from '@tanstack/react-query';

import { getRuntimeConfig } from '@/lib/runtime-config.js';

/**
 * Whether the "Automations in Agent" section is enabled.
 *
 * A plain synchronous read of the unified runtime config (GET /v1/config,
 * `features.automations`) loaded once at boot — not a React hook (no state/effect),
 * so it's named `is*`, not `use*`. Fail-open: the config loader coerces the flag to
 * a boolean and defaults it to `true`, so a missing/malformed value keeps the
 * Automations affordances visible.
 */
export function isAutomationsInAgentEnabled(): boolean {
  return getRuntimeConfig().features.automations;
}

const DEVELOPER_FLAG_ENDPOINT = '/v1/feature-flags/developer';

/**
 * Reads the "show Developer in web" flag from the admin service's public feature-flags endpoint
 * (`GET /v1/feature-flags/developer`, no auth). Fail-open: any error or an absent flag keeps the
 * Developer section visible until an admin explicitly turns it off.
 */
async function fetchDeveloperFlag(): Promise<{ enabled: boolean }> {
  try {
    const res = await fetch(DEVELOPER_FLAG_ENDPOINT, {
      credentials: 'omit',
    });
    if (!res.ok) return { enabled: true };
    const body = (await res.json()) as { enabled?: boolean };
    return { enabled: body.enabled !== false };
  } catch {
    return { enabled: true };
  }
}

export function developerFlagQueryOptions() {
  return queryOptions({
    queryKey: ['feature-flags', 'developer'],
    queryFn: fetchDeveloperFlag,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Authoritative Developer flag read for a write capability; errors and malformed data stay closed. */
export function developerCommandFlagQueryOptions() {
  return queryOptions({
    queryKey: ['feature-flags', 'developer', 'command'],
    queryFn: async (): Promise<{ enabled: boolean }> => {
      const response = await fetch(DEVELOPER_FLAG_ENDPOINT, {
        credentials: 'omit',
      });
      if (!response.ok) throw new Error('Developer flag is unavailable.');
      const body = (await response.json()) as { enabled?: unknown };
      if (typeof body.enabled !== 'boolean') {
        throw new Error('Developer flag response is invalid.');
      }
      return { enabled: body.enabled };
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** React hook for the developer flag — fail-open (visible unless explicitly disabled). */
export function useDeveloperFlag(): boolean {
  const query = useQuery(developerFlagQueryOptions());
  return query.data?.enabled !== false;
}

/** Async read of the developer flag for route guards — fail-open (true on any error). */
export async function isDeveloperEnabled(): Promise<boolean> {
  return (await fetchDeveloperFlag()).enabled;
}
